import Link from '@components/common/Link/Link'
import Button from '@components/ui/Button'
import { LinkFragment } from '@lib/umbraco/generated/types.generated'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import { SplitText } from 'gsap/dist/SplitText'
import { CSSProperties, useEffect, useRef } from 'react'
import s from './ScrollRevealText.module.scss'
export type ScrollRevealTextProps = {
  scrollText?: string
  scrollButton?: LinkFragment
  backgroundColor?: string
  textColor?: string
  animated?: boolean
}

const ScrollRevealText: React.FC<ScrollRevealTextProps> = ({
  scrollText,
  scrollButton,
  backgroundColor,
  textColor,
  animated,
}) => {
  const tl = useRef<GSAPTimeline>()
  const split = useRef<SplitText>()
  const container = useRef<HTMLDivElement>(null)
  const p = useRef<HTMLParagraphElement>(null)
  const btnMask = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!animated) return

    const ctx = gsap.context(() => {
      gsap.registerPlugin(ScrollTrigger, SplitText)

      split.current = new SplitText(p.current, { type: 'words' })

      const masks: HTMLElement[] = []
      const wordBackgrounds = []
      split.current.words.forEach((target, index) => {
        let wordBackground = target.cloneNode(true) as HTMLElement
        wordBackground.classList.add(s.wordBackground)

        let mask = target.cloneNode(true) as HTMLElement
        mask.classList.add(s.mask)

        target.innerHTML = ''
        target.appendChild(wordBackground)
        target.appendChild(mask)

        wordBackgrounds.push(wordBackground)
        masks.push(mask)
      })
      masks.push(btnMask.current!)
      tl.current = gsap.timeline({
        paused: true,
        easing: 'linear',
        scrollTrigger: {
          trigger: container.current,
          start: 'top center',
          end: () => `bottom-=50px bottom`,
          scrub: 0.75,
          invalidateOnRefresh: true,
        },
      })
      masks.forEach((mask) => {
        tl.current &&
          tl.current.add(gsap.fromTo(mask, { autoAlpha: 0 }, { autoAlpha: 1 }))
      })
    })

    return () => ctx.revert()
  }, [])

  return (
    <section
      className={s.root}
      style={
        {
          '--srt-bg-color': backgroundColor,
          '--srt-color':
            textColor.toLowerCase() === 'black' ? 'black' : 'white',
          '--srt-inverse-color':
            textColor.toLowerCase() === 'black' ? 'white' : 'black',
        } as CSSProperties
      }
      ref={container}
    >
      <div className={s.content}>
        <p className={s.text} ref={p}>
          {scrollText}
        </p>
        {scrollButton && (
          <Link link={scrollButton}>
            <div className={s.btnContainer}>
              <span>
                <Button variant="bordered-animate" className={s.btnBackground}>
                  {scrollButton.name}
                </Button>
              </span>
              {animated && (
                <Button
                  variant="bordered-animate"
                  className={s.btnMask}
                  ref={btnMask}
                >
                  {scrollButton.name}
                </Button>
              )}
            </div>
          </Link>
        )}
      </div>
    </section>
  )
}

export default ScrollRevealText
